export type ListNavigationEntity = 'alerts' | 'trips' | 'drivers' | 'devices';

export type ListNavigationEntry = {
  itemId: string;
  routeId: string;
  page: number;
  routeState?: Record<string, unknown>;
};

export type ListNavigationContext = {
  entity: ListNavigationEntity;
  returnPath: string;
  returnState?: unknown;
  pageSize: number;
  isComplete?: boolean;
  fleetId?: string;
  query?: string;
  entries: ListNavigationEntry[];
};

export type ListNavigationSourceItem = {
  _id: string;
  isMergedTrip?: boolean;
  mergedTripId?: string;
};

export type ListNavigationState = {
  contextKey: string;
  itemId: string;
};

export type LocationStateWithListNavigation = {
  listNavigation?: ListNavigationState;
};

const CONTEXT_PREFIX = 'varroc-list-navigation-v1:';
const FOCUS_PREFIX = 'varroc-list-focus-v1:';

const getStorageKey = (prefix: string, key: string): string => `${prefix}${key}`;

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

const readItem = (key: string): string | null => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeItem = (storage: Storage, key: string, value: string): boolean => {
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

const evictOwnedKeys = (storage: Storage, keepKey: string): number => {
  try {
    const removableKeys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      const isOwnedKey = !!key
        && (key.startsWith(CONTEXT_PREFIX) || key.startsWith(FOCUS_PREFIX));
      if (key && isOwnedKey && key !== keepKey) {
        removableKeys.push(key);
      }
    }
    removableKeys.forEach((key) => storage.removeItem(key));
    return removableKeys.length;
  } catch (error) {
    return 0;
  }
};

const parseContext = (raw: string | null): ListNavigationContext | null => {
  if (!raw) return null;

  try {
    return JSON.parse(raw) as ListNavigationContext;
  } catch (error) {
    return null;
  }
};

export const createListNavigationContextKey = (
  entity: ListNavigationEntity,
  pathname: string,
  search: string,
  signature = '',
): string => {
  let hash = 0;
  for (let index = 0; index < signature.length; index += 1) {
    hash = (hash * 31 + signature.charCodeAt(index)) % 2147483647;
  }
  return `${entity}:${pathname}${search}:${hash}`;
};

export const buildListNavigationEntries = (
  items: ListNavigationSourceItem[],
  entity: ListNavigationEntity,
  pageSize: number,
  fixedPage?: number,
): ListNavigationEntry[] => items.map((item, index) => ({
  itemId: item._id,
  routeId: entity === 'trips' && item.isMergedTrip && item.mergedTripId
    ? item.mergedTripId
    : item._id,
  page: fixedPage ?? Math.floor(index / pageSize) + 1,
  routeState: entity === 'trips'
    ? { isMergedTrip: !!item.isMergedTrip }
    : undefined,
}));

export const saveListNavigationContext = (
  contextKey: string,
  context: ListNavigationContext,
): void => {
  const storage = getStorage();
  if (!storage) return;

  let serialized = '';
  try {
    serialized = JSON.stringify(context);
  } catch (error) {
    return;
  }

  const storageKey = getStorageKey(CONTEXT_PREFIX, contextKey);
  if (writeItem(storage, storageKey, serialized)) return;

  evictOwnedKeys(storage, storageKey);
  writeItem(storage, storageKey, serialized);
};

export const isListNavigationContextComplete = (contextKey: string): boolean => {
  const context = parseContext(readItem(getStorageKey(CONTEXT_PREFIX, contextKey)));

  return !!context && context.isComplete === true && Array.isArray(context.entries);
};

export const getListNavigationContext = (
  state: unknown,
  expectedEntity: ListNavigationEntity,
): { state: ListNavigationState; context: ListNavigationContext } | null => {
  if (!state || typeof state !== 'object') return null;

  const { listNavigation } = state as LocationStateWithListNavigation;
  if (!listNavigation?.contextKey || !listNavigation.itemId) return null;

  const context = parseContext(
    readItem(getStorageKey(CONTEXT_PREFIX, listNavigation.contextKey)),
  );
  if (!context || context.entity !== expectedEntity || !Array.isArray(context.entries)) {
    return null;
  }

  return { state: listNavigation, context };
};

export const getListNavigationReturnPath = (
  context: ListNavigationContext,
  entry: ListNavigationEntry,
): string => {
  const pathParts = context.returnPath.split('?');
  const { 0: pathname, 1: search = '' } = pathParts;
  const searchParams = new URLSearchParams(search);
  const originPage = Number(searchParams.get('page') || 1);
  if (entry.page === originPage) return context.returnPath;
  if (entry.page > 1) {
    searchParams.set('page', entry.page.toString());
  } else {
    searchParams.delete('page');
  }
  const nextSearch = searchParams.toString();
  return nextSearch ? `${pathname}?${nextSearch}` : pathname;
};

export const requestListItemFocus = (returnPath: string, itemId: string): void => {
  const storage = getStorage();
  if (!storage) return;

  writeItem(storage, getStorageKey(FOCUS_PREFIX, returnPath), itemId);
};

export const consumeListItemFocus = (returnPath: string): string | null => {
  const storage = getStorage();
  if (!storage) return null;

  const key = getStorageKey(FOCUS_PREFIX, returnPath);
  try {
    const itemId = storage.getItem(key);
    if (itemId) storage.removeItem(key);
    return itemId;
  } catch (error) {
    return null;
  }
};
